import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Briefcase, Building2, FileText, AlertCircle, CheckCircle, Linkedin } from 'lucide-react';
import { validateLinkedInUrl } from '../lib/input-sanitization';

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { profile, updateProfile, loading: authLoading, profileLoading, isProfileComplete } = useAuth();
  const [jobTitle, setJobTitle] = useState(profile?.job_title || '');
  const [company, setCompany] = useState(profile?.company || '');
  const [linkedinUrl, setLinkedinUrl] = useState(profile?.linkedin_url || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (profile) {
      setJobTitle(profile.job_title || '');
      setCompany(profile.company || '');
      setLinkedinUrl(profile.linkedin_url || '');
      setBio(profile.bio || '');
    }
  }, [profile]);

  useEffect(() => {
    console.log('[ProfileSetup] State:', {
      authLoading,
      profileLoading,
      isProfileComplete,
      timestamp: new Date().toISOString()
    });

    // CRITICAL: Wait for BOTH auth AND profile to finish loading
    if (authLoading || profileLoading) {
      console.log('[ProfileSetup] Waiting for auth/profile to load...');
      return;
    }

    // After loading completes, if profile is already complete, redirect to dashboard
    if (isProfileComplete) {
      console.log('[ProfileSetup] ✓ Profile already complete - redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    } else {
      console.log('[ProfileSetup] Profile needs completion - staying on page');
    }
  }, [authLoading, profileLoading, isProfileComplete, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    console.log('[ProfileSetup] Form submission started');

    const trimmedJobTitle = jobTitle.trim();
    const trimmedCompany = company.trim();
    const trimmedLinkedinUrl = linkedinUrl.trim();
    const trimmedBio = bio.trim();

    // Validate required fields
    if (!trimmedJobTitle || !trimmedCompany) {
      const errorMsg = 'Job title and company are required';
      console.error('[ProfileSetup] Validation failed:', errorMsg);
      setError(errorMsg);
      setLoading(false);
      return;
    }

    // Validate LinkedIn URL if provided
    let normalizedLinkedinUrl = trimmedLinkedinUrl;
    if (trimmedLinkedinUrl) {
      const linkedinValidation = validateLinkedInUrl(trimmedLinkedinUrl);
      if (!linkedinValidation.valid) {
        setError(linkedinValidation.error || 'Invalid LinkedIn URL');
        setLoading(false);
        return;
      }
      // Use the normalized URL from validation
      normalizedLinkedinUrl = linkedinValidation.normalizedUrl || trimmedLinkedinUrl;
    }

    console.log('[ProfileSetup] Updating profile with:', {
      hasJobTitle: !!trimmedJobTitle,
      hasCompany: !!trimmedCompany,
      hasLinkedinUrl: !!normalizedLinkedinUrl,
      hasBio: !!trimmedBio
    });

    try {
      const { error: updateError } = await updateProfile({
        job_title: trimmedJobTitle,
        company: trimmedCompany,
        linkedin_url: normalizedLinkedinUrl || null,
        bio: trimmedBio || '',
      });

      if (updateError) {
        console.error('[ProfileSetup] Update failed:', updateError);
        setError(updateError.message || 'Failed to update profile. Please try again.');
        setLoading(false);
      } else {
        console.log('[ProfileSetup] Profile updated successfully');
        setSuccess(true);

        // The updateProfile function in AuthContext already reloads the profile
        // We just need a small delay for the success message, then redirect
        setTimeout(() => {
          console.log('[ProfileSetup] Navigating to dashboard after profile completion');
          setLoading(false);
          navigate('/dashboard', { replace: true });
        }, 1000);
      }
    } catch (err: any) {
      console.error('[ProfileSetup] Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  // Profile completion status from AuthContext (centralized check)
  const isComplete = isProfileComplete;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-semibold text-[#1F2937] mb-2">
            Complete Your Profile
          </h2>
          <p className="text-[#6B7280]">
            Help others understand your background when they provide feedback.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-2">
              Job Title
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D1D5DB]" />
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                placeholder="e.g., Senior Product Manager"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-2">
              Company
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D1D5DB]" />
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                placeholder="e.g., Acme Corp"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-2">
              LinkedIn Profile
              <span className="text-[#9CA3AF] font-normal ml-1">(Optional)</span>
            </label>
            <div className="relative">
              <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D1D5DB]" />
              <input
                type="text"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition"
                placeholder="www.linkedin.com/in/yourname"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-2">
              Professional Bio
              <span className="text-[#9CA3AF] font-normal ml-1">(Optional)</span>
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 w-5 h-5 text-[#D1D5DB]" />
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition resize-none"
                placeholder="Tell us about your role, experience, and what you're working on..."
                rows={4}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">Profile updated successfully!</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#8CAFA8] text-white py-3 rounded-lg font-semibold hover:bg-[#7A9B94] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? 'Saving...' : isComplete ? 'Update Profile' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
}
